import { useEffect, useMemo, useState } from 'react'
import { postDataAPI } from '../../../lib/api'
import type { SignupUser } from '../SignupPage'
import OnboardingShell from './OnboardingShell'
import TextField from '../../../components/ui/TextField'
import { OnboardingStep } from './steps'

export default function Step02VerifyOtp({
  setStep,
  user,
  setUser,
}: {
  setStep: (n: number) => void
  user: SignupUser
  setUser: React.Dispatch<React.SetStateAction<SignupUser>>
}) {
  const [otp, setOtp] = useState('')
  const [resendTimer, setResendTimer] = useState(0)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const email = user?.email || ''

  useEffect(() => {
    if (resendTimer <= 0) return
    const t = window.setTimeout(() => setResendTimer((s) => s - 1), 1000)
    return () => window.clearTimeout(t)
  }, [resendTimer])

  const canSubmit = useMemo(() => otp.trim().length === 6 && !isVerifying, [otp, isVerifying])

  const verify = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return
    if (otp.trim().length !== 6) return
    setError(null)
    setIsVerifying(true)
    try {
      const res = await postDataAPI('auth/verifyOtpForEmail', { email, code: otp.trim() })
      if (res?.status === 200) {
        // After OTP verification, determine onboarding path:
        // - default (not invited): artist flow
        // - invited: manager/support flow
        try {
          const invitedRes = await postDataAPI('team/invited-artists', { teamMemberEmail: email })
          // Support multiple backend response shapes
          const raw =
            invitedRes?.data?.data ??
            invitedRes?.data ??
            (Array.isArray(invitedRes) ? invitedRes : null)
          const invitedArtists = Array.isArray(raw) ? raw : []

          if (invitedArtists.length > 0) {
            const first = invitedArtists[0] || {}
            const inferredRole = (first.role || first.accountType || first.invitedRole || first.teamMemberRole || '').toString().toLowerCase()
            const role = inferredRole === 'manager' || inferredRole === 'support' ? inferredRole : 'support'

            setUser((prev) => ({
              ...prev,
              professionRole: role,
              teamMemberPlan: role === 'manager' ? 'basic' : 'support',
              availableArtists: invitedArtists,
            }))
            setStep(OnboardingStep.TeamJoin)
            return
          }
        } catch (e) {
          console.warn('Invite check failed, continuing as artist:', e)
        }

        setUser((prev) => ({ ...prev, professionRole: 'artist', plan: -1 }))
        setStep(OnboardingStep.FindPresence)
      } else {
        setError(res?.message || 'Verification failed')
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Verification failed')
    } finally {
      setIsVerifying(false)
    }
  }

  const resend = async () => {
    if (!email) return
    if (resendTimer > 0) return
    setError(null)
    setIsResending(true)
    try {
      await postDataAPI('auth/sendOtpToEmail', { email })
      setResendTimer(30)
      setOtp('')
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to resend code')
    } finally {
      setIsResending(false)
    }
  }

  const invitedHint = user?.invitedFromQuery
    ? "You've been invited to join a team. Verify your email to continue."
    : 'Check your email inbox and spam folder for the verification code.'

  return (
    <OnboardingShell title={`Enter the code sent to ${email}`} footer={invitedHint}>
      <form onSubmit={verify} className="space-y-5">
        <TextField
          value={otp}
          onChange={(v) => setOtp(v.replace(/[^0-9]/g, '').slice(0, 6))}
          placeholder="6-digit code"
          inputMode="numeric"
          error={error}
        />

        <button type="submit" disabled={!canSubmit} className="ds-btn ds-btn-primary w-full">
          {isVerifying ? 'Verifying…' : 'Verify code'}
        </button>
      </form>

      <div className="mt-5">
        <button type="button" onClick={resend} disabled={isResending || resendTimer > 0} className="text-sm text-gray-500 hover:text-gray-900 disabled:opacity-50">
          {resendTimer > 0 ? `Resend in ${resendTimer}s` : isResending ? 'Resending…' : 'Resend code'}
        </button>
      </div>
    </OnboardingShell>
  )
}

